const DRAG_THRESHOLD = 3;
const COMPATIBILITY_EVENT_TIMEOUT = 1000;

type SelectionGesture = {
  pointerId: number;
  startX: number;
  startY: number;
  dragging: boolean;
  pointerIsDown: boolean;
  control: HTMLInputElement | HTMLTextAreaElement | HTMLElement;
  selection?: {
    start: number;
    end: number;
    direction: 'forward' | 'backward' | 'none';
  };
};

function getTextControl(target: EventTarget | null) {
  if (!(target instanceof Element)) {
    return null;
  }

  const textarea = target.closest('textarea');

  if (textarea instanceof HTMLTextAreaElement) {
    return textarea;
  }

  const input = target.closest('input');

  if (input instanceof HTMLInputElement) {
    return ['email', 'password', 'search', 'tel', 'text', 'url'].includes(input.type) ? input : null;
  }

  const editable = target.closest<HTMLElement>('[contenteditable]:not([contenteditable="false"])');
  return editable ?? null;
}

function stopApplicationHandling(event: Event, preventDefault = false) {
  if (preventDefault) {
    event.preventDefault();
  }

  event.stopImmediatePropagation();
}

/**
 * Isolates mouse/pointer events produced by a text-selection drag.
 *
 * The events are stopped at window capture, before backdrops and document-level
 * outside-click handlers can react. Their default action is preserved until the
 * final click, so the browser can continue extending the native text selection.
 */
export function setupTextSelectionGuard(target: Window = window) {
  let gesture: SelectionGesture | null = null;
  let suppressCompatibilityEvents = false;
  let resetTimer: ReturnType<typeof setTimeout> | undefined;

  function clearResetTimer() {
    if (resetTimer !== undefined) {
      clearTimeout(resetTimer);
      resetTimer = undefined;
    }
  }

  function reset() {
    clearResetTimer();

    if (gesture?.control.hasPointerCapture?.(gesture.pointerId)) {
      try {
        gesture.control.releasePointerCapture(gesture.pointerId);
      } catch {}
    }

    gesture = null;
    suppressCompatibilityEvents = false;
  }

  function scheduleReset() {
    clearResetTimer();
    resetTimer = setTimeout(reset, COMPATIBILITY_EVENT_TIMEOUT);
  }

  function saveSelection() {
    const control = gesture?.control;

    if (
      !(control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) ||
      control.selectionStart == null ||
      control.selectionEnd == null
    ) {
      return;
    }

    gesture!.selection = {
      start: control.selectionStart,
      end: control.selectionEnd,
      direction: control.selectionDirection ?? 'none',
    };
  }

  function restoreSelection() {
    const control = gesture?.control;
    const selection = gesture?.selection;

    if (
      !(control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) ||
      !selection ||
      !control.isConnected
    ) {
      return;
    }

    control.focus({ preventScroll: true });
    control.setSelectionRange(selection.start, selection.end, selection.direction);
  }

  function onPointerDown(event: PointerEvent) {
    reset();

    const control = getTextControl(event.target);

    if (event.button !== 0 || !event.isPrimary || !control) {
      return;
    }

    gesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      dragging: false,
      pointerIsDown: true,
      control,
    };

    saveSelection();

    try {
      control.setPointerCapture(event.pointerId);
    } catch {}
  }

  function onPointerMove(event: PointerEvent) {
    if (!gesture?.pointerIsDown || event.pointerId !== gesture.pointerId) {
      return;
    }

    if (!gesture.dragging) {
      const distance = Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY);
      gesture.dragging = distance >= DRAG_THRESHOLD;
    }

    if (gesture.dragging) {
      suppressCompatibilityEvents = true;
      stopApplicationHandling(event);
      queueMicrotask(saveSelection);
    }
  }

  function onPointerUp(event: PointerEvent) {
    if (!gesture || event.pointerId !== gesture.pointerId) {
      return;
    }

    if (gesture.dragging) {
      gesture.pointerIsDown = false;
      suppressCompatibilityEvents = true;
      stopApplicationHandling(event);
      queueMicrotask(saveSelection);
      scheduleReset();
    } else {
      reset();
    }
  }

  function onPointerCancel(event: PointerEvent) {
    if (gesture?.pointerId !== event.pointerId) {
      return;
    }

    if (gesture.dragging) {
      stopApplicationHandling(event);
    }

    reset();
  }

  function onCompatibilityEvent(event: Event) {
    if (suppressCompatibilityEvents) {
      stopApplicationHandling(event);
    }
  }

  function onClick(event: MouseEvent) {
    if (!suppressCompatibilityEvents) {
      return;
    }

    stopApplicationHandling(event, true);
    restoreSelection();
    reset();
  }

  function onDragStart(event: DragEvent) {
    if (!gesture?.dragging) {
      return;
    }

    stopApplicationHandling(event, true);
  }

  function onSelect(event: Event) {
    if (event.target === gesture?.control) {
      saveSelection();
    }
  }

  function onBlur(event: FocusEvent) {
    if (event.target === target) {
      reset();
      return;
    }

    if (event.target !== gesture?.control || !gesture.pointerIsDown || !gesture.dragging) {
      return;
    }

    saveSelection();
    stopApplicationHandling(event);
    queueMicrotask(restoreSelection);
  }

  target.addEventListener('pointerdown', onPointerDown, true);
  target.addEventListener('pointermove', onPointerMove, true);
  target.addEventListener('pointerup', onPointerUp, true);
  target.addEventListener('pointercancel', onPointerCancel, true);
  target.addEventListener('mousemove', onCompatibilityEvent, true);
  target.addEventListener('mouseup', onCompatibilityEvent, true);
  target.addEventListener('click', onClick, true);
  target.addEventListener('dragstart', onDragStart, true);
  target.addEventListener('select', onSelect, true);
  target.addEventListener('blur', onBlur, true);

  return () => {
    reset();
    target.removeEventListener('pointerdown', onPointerDown, true);
    target.removeEventListener('pointermove', onPointerMove, true);
    target.removeEventListener('pointerup', onPointerUp, true);
    target.removeEventListener('pointercancel', onPointerCancel, true);
    target.removeEventListener('mousemove', onCompatibilityEvent, true);
    target.removeEventListener('mouseup', onCompatibilityEvent, true);
    target.removeEventListener('click', onClick, true);
    target.removeEventListener('dragstart', onDragStart, true);
    target.removeEventListener('select', onSelect, true);
    target.removeEventListener('blur', onBlur, true);
  };
}
